<script setup lang="ts">
import { computed, ref, onBeforeMount } from "vue";
import { useI18n } from "vue-i18n";
import { ucfirst } from "chartjs-graph";
import { RuleDimension } from "@/modules/rule_engine/ruleEngineClasses";
import { required } from "@/ui/validation/validationRules";
import ConfigureConditionComparisonSelectionDatePicker from "@/modules/rule_engine/components/conditions/ConfigureConditionComparisonSelectionDatePicker.vue";
import dayjs from "dayjs";
import { parse } from "chartjs-graph";
const { t } = useI18n();
const dateFormat = "YYYY-MM-DD HH:mm:ss";

const props = defineProps<{
	type:
		| "comparison_string"
		| "comparison_number"
		| "comparison_date"
		| "comparison_boolean";
	selectedRuleDimension: RuleDimension;
}>();

export type ComparisonSelection = {
	selectedOperator: string;
	/** `comparison_boolean` stores a real boolean, not "true"/"false". */
	selectedValue: string | number | boolean;
};

const selection = defineModel<ComparisonSelection>();
const isInputValid = defineModel<boolean>("isInputValid", {
	default: () => ref(true),
});

/**
 * Seeds a date so the field opens on today rather than empty.
 *
 * Assigns the whole model: the parent binds `selection` to a computed whose
 * getter builds a fresh object every read, so mutating a property of it writes
 * into a throwaway and never reaches the computed's setter.
 */
onBeforeMount(() => {
	if (
		props.type === "comparison_date" &&
		!isDateString(selection.value.selectedValue)
	) {
		selection.value = {
			selectedOperator: selection.value.selectedOperator ?? "equal_to",
			selectedValue: dayjs(new Date()).startOf("day").format(dateFormat),
		};
	}
});

const isDateString = (value: unknown): boolean => {
	if (typeof value !== "string") return false;

	return parse(value)?.isValid();
};

const selectedValue = computed({
	get() {
		return selection.value.selectedValue ?? 0;
	},
	set(newValue: string | number | boolean | Date) {
		let parsedValue: string | number | boolean | Date;

		// One chain: an unchained second `if` meant the boolean branch was always
		// overwritten by `String(newValue)`, storing "true"/"false".
		if (props.type === "comparison_boolean") {
			parsedValue = newValue;
		} else if (props.type === "comparison_number") {
			parsedValue =
				typeof newValue === "string" ? parseFloat(newValue) : Number(newValue);
			if (isNaN(parsedValue)) parsedValue = 0;
		} else {
			parsedValue = String(newValue);
		}

		selection.value = {
			selectedOperator: selection.value.selectedOperator,
			selectedValue: parsedValue,
		};
	},
});

const selectedOperator = computed({
	get() {
		return selection.value.selectedOperator ?? "equal_to";
	},
	set(newValue: string) {
		selection.value = {
			selectedOperator: newValue,
			selectedValue: selection.value.selectedValue ?? 0,
		};
	},
});

const operatorOptions = computed(() => {
	if (props.type === "comparison_string") {
		return ["equal_to", "not_equal_to"];
	} else if (props.type === "comparison_boolean") {
		return ["equal_to"];
	}

	return [
		"less_than",
		"less_than_or_equal_to",
		"equal_to",
		"not_equal_to",
		"greater_than",
		"greater_than_or_equal_to",
	];
});

/**
 * The field shows a symbol to stay narrow; the dropdown list and the condition
 * summary above the editor both spell the operator out in words.
 */
const OPERATOR_SYMBOLS: Record<string, string> = {
	less_than: "<",
	less_than_or_equal_to: "≤",
	equal_to: "=",
	not_equal_to: "≠",
	greater_than: ">",
	greater_than_or_equal_to: "≥",
};

const operatorLabel = (operator: string) =>
	ucfirst(t(`rule_engine.condition_operators.${operator}`));

/** A single permitted operator is not a choice, so it is not offered. */
const hasOperatorChoice = computed(() => operatorOptions.value.length > 1);
</script>

<template>
	<div class="d-flex ga-2 align-start">
		<v-select
			v-if="hasOperatorChoice"
			class="operator"
			variant="filled"
			density="compact"
			hide-details="auto"
			:items="operatorOptions"
			:item-title="(operatorOption) => operatorLabel(operatorOption)"
			:item-value="(operatorOption) => operatorOption"
			:aria-label="operatorLabel(selectedOperator)"
			v-tooltip="operatorLabel(selectedOperator)"
			v-model="selectedOperator"
		>
			<template v-slot:selection="{ item }">
				<span class="operator-symbol">{{ OPERATOR_SYMBOLS[item.value] }}</span>
			</template>
			<template v-slot:item="{ props: itemProps, item }">
				<v-list-item v-bind="itemProps">
					<template v-slot:prepend>
						<span class="operator-symbol">{{
							OPERATOR_SYMBOLS[item.value]
						}}</span>
					</template>
				</v-list-item>
			</template>
		</v-select>

		<v-text-field
			v-if="
				props.type === 'comparison_number' || props.type === 'comparison_string'
			"
			class="value"
			:placeholder="
				props.type === 'comparison_number' ? 'Numeric value' : 'Value'
			"
			v-model="selectedValue"
			variant="filled"
			density="compact"
			hide-details="auto"
			:rules="[required(t)]"
		/>

		<configure-condition-comparison-selection-date-picker
			v-else-if="props.type === 'comparison_date'"
			class="value"
			v-model="selectedValue"
			v-model:is-input-valid="isInputValid"
		/>

		<v-select
			v-else-if="props.type === 'comparison_boolean'"
			class="value"
			variant="filled"
			density="compact"
			hide-details="auto"
			:items="[
				{ title: 'true', value: true },
				{ title: 'false', value: false },
			]"
			v-model="selectedValue"
			:rules="[required(t)]"
		/>
	</div>
</template>

<style scoped lang="scss">
.operator {
	flex: 0 0 auto;
	width: 72px;
}

.value {
	flex: 1 1 auto;
	min-width: 0;
}

.operator-symbol {
	display: inline-block;
	width: 1.5rem;
	font-weight: 600;
}
</style>
